import { useCallback, useMemo, useState } from "react";
import { invalidateForm1099NecReport } from "@api/form1099NecReport.api";
import { ChartOfAccountsDrawer } from "@components/accounting/chartOfAccounts/ChartOfAccountsDrawer";
import { CounterpartiesDrawer } from "@components/settings/Directory/CounterpartiesDrawer";
import { useScope } from "@state/scopeContext.lib";

export type Form1099NecDrawer = "mapping" | "counterparties";

interface UseForm1099NecDrawersParams {
    selectedEntity: { id: number } | undefined;
    availableEntities: Array<{ id: number }>;
}

interface UseForm1099NecDrawersResult {
    openDrawer: Form1099NecDrawer | null;
    mappingEntityIds: number[];
    openMappingDrawer: () => void;
    openCounterpartiesDrawer: () => void;
    closeMappingDrawer: () => void;
    closeCounterpartiesDrawer: () => void;
}

export function useForm1099NecDrawers({
    selectedEntity,
    availableEntities,
}: UseForm1099NecDrawersParams): UseForm1099NecDrawersResult {
    const { workspaceId } = useScope();
    const [openDrawer, setOpenDrawer] = useState<Form1099NecDrawer | null>(
        null,
    );
    const mappingEntityIds = useMemo(
        () =>
            selectedEntity
                ? [selectedEntity.id]
                : availableEntities.map((entity) => entity.id),
        [selectedEntity, availableEntities],
    );
    const openMappingDrawer = useCallback(() => setOpenDrawer("mapping"), []);
    const openCounterpartiesDrawer = useCallback(
        () => setOpenDrawer("counterparties"),
        [],
    );
    const closeMappingDrawer = useCallback(() => setOpenDrawer(null), []);
    const closeCounterpartiesDrawer = useCallback(() => {
        setOpenDrawer(null);
        if (workspaceId) {
            void invalidateForm1099NecReport(workspaceId);
        }
    }, [workspaceId]);

    return {
        openDrawer,
        mappingEntityIds,
        openMappingDrawer,
        openCounterpartiesDrawer,
        closeMappingDrawer,
        closeCounterpartiesDrawer,
    };
}

interface Form1099NecDrawersProps {
    drawers: UseForm1099NecDrawersResult;
}

export const Form1099NecDrawers = ({ drawers }: Form1099NecDrawersProps) => (
    <>
        <CounterpartiesDrawer
            open={drawers.openDrawer === "counterparties"}
            onClose={drawers.closeCounterpartiesDrawer}
        />
        <ChartOfAccountsDrawer
            open={drawers.openDrawer === "mapping"}
            onClose={drawers.closeMappingDrawer}
            entityIds={drawers.mappingEntityIds}
        />
    </>
);
